import React from "react";
import { Facebook, Instagram, Linkedin, Twitter } from "lucide-react";

const WhatsAppIcon = ({ size = 17 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.47 14.38c-.3-.15-1.75-.86-2.02-.96-.27-.1-.47-.15-.67.15-.2.3-.77.96-.94 1.16-.17.2-.35.22-.64.07-.3-.15-1.27-.47-2.42-1.49-.9-.8-1.5-1.79-1.67-2.09-.17-.3-.02-.46.13-.6.13-.14.3-.37.45-.55.15-.19.2-.32.3-.52.1-.2.05-.37-.02-.52-.08-.15-.68-1.6-.93-2.19-.24-.58-.49-.5-.67-.5h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.01-1.04 2.46 0 1.45 1.06 2.85 1.2 3.05.15.2 2.07 3.17 5.02 4.44.7.3 1.25.48 1.68.62.7.22 1.35.19 1.86.12.56-.09 1.73-.71 1.98-1.4.25-.68.25-1.27.17-1.39-.07-.12-.27-.2-.57-.35zM12.04 21.5h-.01a9.4 9.4 0 0 1-4.79-1.31l-.34-.2-3.56.93.95-3.47-.22-.36a9.38 9.38 0 0 1-1.44-5 9.47 9.47 0 0 1 16.17-6.7 9.38 9.38 0 0 1 2.77 6.7c-.01 5.2-4.25 9.41-9.53 9.41zM12.05 1A11.4 11.4 0 0 0 .64 12.36c0 2 .53 3.96 1.53 5.68L.5 24l6.15-1.6a11.45 11.45 0 0 0 5.4 1.37h.01c6.3 0 11.43-5.11 11.44-11.4A11.3 11.3 0 0 0 20.13 4.3A11.36 11.36 0 0 0 12.05 1z" />
  </svg>
);

const ThreadsIcon = ({ size = 17 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16.2 11.14a6.7 6.7 0 0 0-.25-.11c-.15-2.72-1.63-4.28-4.13-4.3h-.03c-1.49 0-2.73.64-3.5 1.8l1.37.94c.57-.87 1.47-1.05 2.13-1.05h.02c.82 0 1.44.24 1.84.71.29.34.49.82.58 1.42a10.7 10.7 0 0 0-2.4-.12c-2.42.14-3.98 1.55-3.87 3.51.05 1 .55 1.85 1.4 2.4.72.47 1.64.7 2.6.65 1.27-.07 2.25-.55 2.93-1.44.52-.67.85-1.54 1-2.64.61.37 1.06.85 1.31 1.43.43.99.45 2.61-.87 3.93-1.16 1.16-2.55 1.66-4.65 1.67-2.33-.02-4.09-.77-5.24-2.22-1.07-1.36-1.63-3.32-1.65-5.83.02-2.51.58-4.47 1.65-5.83 1.15-1.45 2.91-2.2 5.24-2.22 2.35.02 4.13.77 5.3 2.23.57.72 1 1.62 1.29 2.67l1.64-.44c-.35-1.29-.9-2.4-1.64-3.33C17.94 2.94 15.7 2 12.86 2h-.01C10 2.01 7.8 2.96 6.3 4.83 4.96 6.5 4.27 8.82 4.25 11.72v.02c.02 2.9.71 5.22 2.05 6.89 1.5 1.87 3.7 2.82 6.55 2.83h.01c2.53-.02 4.31-.68 5.78-2.14 1.92-1.92 1.86-4.33 1.23-5.8-.45-1.06-1.31-1.92-2.48-2.5zm-4.31 4.4c-1.07.06-2.18-.42-2.23-1.42-.04-.74.53-1.57 2.3-1.67.2-.01.4-.02.6-.02.64 0 1.24.06 1.79.18-.2 2.56-1.4 2.87-2.46 2.93z" />
  </svg>
);

const PLATFORMS = [
  { key: "whatsapp_number", label: "WhatsApp", Icon: WhatsAppIcon, href: (v) => `https://wa.me/${v.replace(/\D/g, "")}` },
  { key: "social_instagram", label: "Instagram", Icon: Instagram },
  { key: "social_linkedin", label: "LinkedIn", Icon: Linkedin },
  { key: "social_facebook", label: "Facebook", Icon: Facebook },
  { key: "social_twitter", label: "X", Icon: Twitter },
  { key: "social_threads", label: "Threads", Icon: ThreadsIcon },
];

const link = (value) => {
  const v = (value || "").trim();
  if (!v) return "";
  return /^https?:\/\//i.test(v) ? v : `https://${v}`;
};

/**
 * Social row driven by Admin → Settings → Social media; clearing a field hides that
 * icon immediately, with no code change. `compact` is the small set that rides in the
 * top bar next to the cart, the default size sits in the footer.
 */
export const SocialLinks = ({ settings = {}, compact = false, className = "" }) => {
  const shown = PLATFORMS.map((p) => {
    const raw = (settings[p.key] || "").trim();
    if (!raw) return null;
    return { ...p, url: p.href ? p.href(raw) : link(raw) };
  }).filter(Boolean);

  if (shown.length === 0) return null;

  const wrap = compact
    ? `flex items-center gap-1 ${className}`
    : `flex items-center gap-2.5 mt-5 ${className}`;
  const item = compact
    ? "w-8 h-8 rounded-full text-muted-foreground flex items-center justify-center hover:text-primary hover:bg-primary/10 hover:-translate-y-0.5 transition-all"
    : "w-10 h-10 rounded-full border border-border text-muted-foreground flex items-center justify-center hover:border-primary/50 hover:text-primary hover:-translate-y-0.5 transition-all";

  return (
    <div className={wrap} data-testid={compact ? "topbar-social" : "footer-social"}>
      {shown.map(({ key, label, Icon, url }) => (
        <a
          key={key}
          href={url}
          target="_blank"
          rel="noreferrer"
          title={label}
          aria-label={label}
          data-testid={`${compact ? "topbar" : "social"}-${label.toLowerCase()}`}
          className={item}
        >
          <Icon size={compact ? 15 : 17} />
        </a>
      ))}
    </div>
  );
};

export default SocialLinks;
